import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppHeader } from '@/components/layout/app-header'
import { Main } from '@/components/layout/main'
import { ChatProvider } from './context/ChatContext'
import { useChatContext } from './hooks/useChatContext'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { useState, useEffect } from 'react'
import { MemoryDialog } from './components/memory'
import { useNavigate, useLocation } from '@tanstack/react-router'
import WorkstationChat from './components/ask/chat-content'
import { HistoryDialog } from './components/history'

interface ChatProps {
  openMemoryDialog?: boolean
}

const Chat = ({ openMemoryDialog = false }: ChatProps) => {
  const [isMemoryDialogOpen, setIsMemoryDialogOpen] = useState(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
  const { handleNewChat } = useChatContext()
  const { t } = useTranslation()
  const navigate = useNavigate({ from: '/' })
  const location = useLocation()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const shouldOpenMemory = openMemoryDialog || urlParams.get('memory') === 'true'
    const shouldOpenHistory = urlParams.get('history') === 'true'

    if (shouldOpenMemory) {
      setIsMemoryDialogOpen(true)
      if (urlParams.get('memory') === 'true') {
        setTimeout(() => {
          navigate({
            search: (prev) => {
              const newParams = new URLSearchParams(prev)
              newParams.delete('memory')
              return newParams.toString()
            },
            replace: true
          })
        }, 100)
      }
    }
    if (shouldOpenHistory) {
      setIsHistoryDialogOpen(true)
      if (urlParams.get('history') === 'true') {
        setTimeout(() => {
          navigate({
            search: (prev) => {
              const newParams = new URLSearchParams(prev)
              newParams.delete('history')
              return newParams.toString()
            },
            replace: true
          })
        }, 100)
      }
    }
  }, [openMemoryDialog, location, navigate])

  const handleDialogChange = (open: boolean) => {
    setIsMemoryDialogOpen(open)
  }

  const handleHistoryDialogChange = (open: boolean) => {
    setIsHistoryDialogOpen(open)
  }

  return (
    <>
      <AppHeader fixed />
      <Main>
        <div className='mb-3 flex flex-wrap items-center justify-between space-y-2'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>Workstation</h2>
            <p className='text-muted-foreground'>
              {t(tokens.workstation.subtitle)}
            </p>
          </div>
          <div className='flex gap-2'>
            <Button onClick={handleNewChat}>
              <Plus size={18} />
              <span>{t(tokens.workstation.newchat)}</span>
            </Button>
          </div>
        </div>
        <WorkstationChat />
      </Main>

      <MemoryDialog 
        open={isMemoryDialogOpen}
        onOpenChange={handleDialogChange}
      />
      <HistoryDialog 
        open={isHistoryDialogOpen}
        onOpenChange={handleHistoryDialogChange}
      />
    </>
  )
}

const ChatWithProvider = (props: ChatProps) => (
  <ChatProvider>
    <Chat {...props} />
  </ChatProvider>
)

export default ChatWithProvider
